import "./NewHome.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBox,
  faCircleCheck,
  faClipboardCheck,
  faDollarSign,
  faFilter,
  faMessage,
  faMobileScreenButton,
  faUsers,
  faComment,
  faShare,
  faEye,
  faHeart,
  faBolt,
  faPaperPlane,
  faFileSignature,
  faCalendarCheck,
  faChevronLeft,
  faChevronRight,
  faPlay,
  faCartShopping,
  faPlus,
  faUpload,
  faHandshake,
  faTag,
  faBullseye,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { CollabsState } from "redux-context/collabs";
import { UserState } from "redux-context/user";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { formatNumberKMBT } from "pages/influencers/InfluencerCard";
import { ReactComponent as InstagramIcon } from "assets/images/instagram-icon.svg";
import { ReactComponent as TikTokIcon } from "assets/images/tiktok-icon.svg";
import { ReactComponent as BullhornIcon } from "assets/images/bullhorn-icon.svg";
import { Collab } from "interfaces/collab";
import { getCollabsStats } from "services/SauceService";
import { openNewTabSafe } from "helpers/url-helpers";
import { BusinessCampaignCollab } from "enums/BusinessCampaingCollab";
import { BusinessState } from "redux-context/business";
import PendingActivities from "components/PendingActivities/PendingActivities";
import Loader from "components/loader/Loader";
import { OnboardingStep } from "enums/OnboardingStep";
import { CheckOutlined } from "@ant-design/icons";
import { isMobile } from "react-device-detect";

export const performanceScoreColors = (score: number) => {
  if (score >= 80) return "#10b981";
  if (score >= 50) return "#f6a623";
  return "#ef4444";
};

interface HomeData {
  applied: {
    premium: number;
    basic: number;
    total: number;
    images: {
      avatarUrl: string;
      isPremium: boolean;
    }[];
  };
  booked: {
    premium: number;
    basic: number;
    total: number;
    images: {
      avatarUrl: string;
      isPremium: boolean;
    }[];
  };
  invited: {
    premium: number;
    basic: number;
    total: number;
    sumFollowers?: number;
    latestSentAt: string;
    totalViews: number;
    images: {
      avatarUrl: string;
      isPremium: boolean;
    }[];
  };
  pendingReview: {
    total: number;
  };
  collabs: {
    totalViews: number;
    totalLikes: number;
    totalReach: number;
    totalComments: number;
    totalDinner: number;
    totalReshares: number;
    totalCPM: number;
    dinnerByPosts: any[]; // You can replace `any` with a specific type if you have more information about the structure of this array
    postsCollab: {
      engagementRate: number;
      socialMediaUrl: string;
      reach: number;
      likes: number;
      dinner: number;
      coverImg: string;
      socialMediaName: string;
      ownerUsername: string;
      venueName: string;
      createdAt: string;
      performanceScore: number;
    }[]; // You can replace `any` with a specific type if you have more information about the structure of this array
    averageEngagementRate: number;
  };
  upcoming: {
    [year: string]: {
      [month: string]: Collab[];
    };
  };
}

const FeatureItem: React.FC<{
  icon?: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, children }) => (
  <div className="feature-item">
    <div className="feature-icon">{icon}</div>
    <div>{children}</div>
  </div>
);

const NewHome: React.FC = () => {
  const [homeData, setHomeData] = useState<HomeData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const booked = homeData?.booked || {
    premium: 0,
    basic: 0,
    total: 0,
    images: [],
  };
  const invited = homeData?.invited || {
    premium: 0,
    basic: 0,
    total: 0,
    totalViews: 0,
    latestSentAt: "",
    images: [],
  };
  const collabs = homeData?.collabs || {
    totalViews: 0,
    totalLikes: 0,
    totalReach: 0,
    totalComments: 0,
    totalDinner: 0,
    totalReshares: 0,
    totalCPM: 0,
    dinnerByPosts: [],
    postsCollab: [],
    averageEngagementRate: 0,
  };
  const pendingReview = homeData?.pendingReview || { total: 0 };

  const navigate = useNavigate();
  const { business } = useSelector((state: BusinessState) => state.business);
  const postsContainerRef = useRef<HTMLDivElement>(null);
  const [sortBy, setSortBy] = useState<string>("performance");
  const { numbers } = useSelector((state: CollabsState) => state.collabs);
  const { loggedUser } = useSelector((state: UserState) => state.user);

  const showOnboarding = useMemo(() => {
    return numbers.totalCollabs === 0 && numbers.totalInvited === 0;
  }, [numbers]);

  useEffect(() => {
    if (business?.onboardingStep === OnboardingStep.NotStarted) {
      navigate("/welcome");
    }
  });

  const onClickOpenPost = (url: string) => {
    if (!url) return;
    openNewTabSafe(url);
  };

  const scrollPosts = (direction: "left" | "right") => {
    if (postsContainerRef.current) {
      const scrollAmount = 300; // Adjust this value as needed
      const currentScroll = postsContainerRef.current.scrollLeft;
      const targetScroll =
        direction === "left"
          ? currentScroll - scrollAmount
          : currentScroll + scrollAmount;

      postsContainerRef.current.scrollTo({
        left: targetScroll,
        behavior: "smooth",
      });
    }
  };

  const scrollPostsToStart = () => {
    if (postsContainerRef.current) {
      postsContainerRef.current.scrollTo({
        left: 0,
        behavior: "smooth",
      });
    }
  };

  const sortedPosts = useMemo(() => {
    if (!collabs?.postsCollab) return [];

    const posts = [...collabs.postsCollab];

    switch (sortBy) {
      case "performance":
        return posts.sort(
          (a, b) => (b.performanceScore || 0) - (a.performanceScore || 0),
        );
      case "engagement":
        return posts.sort(
          (a, b) => (b.engagementRate || 0) - (a.engagementRate || 0),
        );
      case "reach":
        return posts.sort((a, b) => (b.reach || 0) - (a.reach || 0));
      case "intent":
        return posts.sort((a, b) => (b.dinner || 0) - (a.dinner || 0));
      case "latest":
      default:
        return posts; // Keep original order for latest
    }
  }, [collabs?.postsCollab, sortBy]);

  const handleSortChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSortBy(event.target.value);
    console.log("scroll");
    scrollPostsToStart();
  };

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        const businessId = localStorage.getItem("businessId");

        const params = { businessId };
        const reportInfo: any = await getCollabsStats(params);
        if (mounted) setHomeData(reportInfo);
      } catch (err: any) {
        console.error("Error loading home data:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, []);

  // if (loading) return <Spin />;

  if (showOnboarding)
    return (
      <div className={`new-home ${isMobile ? "mobile" : ""}`}>
        <div className="container">
          <section className={`hero ${isMobile ? "mobile" : ""}`} >
            <h2 className="hero-title" style={{ textAlign: "center" }}>
              Get Started With Content Marketing
            </h2>
            <p className="hero-desc">
              Choose how you want to connect with creators and grow your
              business through authentic partnerships
            </p>
          </section>

          <div className="cards">
            <div className="cards-grid">
              {/* Card 1 */}
              <div className="card">
                <div className="card-head">
                  <div className="avatar-stack">
                    <img
                      className="avatar"
                      src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-1.jpg"
                      alt="Creator 2"
                    />
                    <img
                      className="avatar"
                      src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-5.jpg"
                      alt="Creator 3"
                    />
                    <img
                      className="avatar"
                      src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-3.jpg"
                      alt="Creator 1"
                    />
                  </div>
                  <div className="badge badge--green">
                    <CheckOutlined />
                    Pre-approved
                  </div>
                </div>

                <h3 className="card-title">
                  I Want to Personally Pick Influencers
                </h3>
                <p className="card-desc">
                  Manually select specific influencers and send personalized
                  collaboration invites with custom product and cash
                  compensation tailored to each creator.
                </p>

                <div className="features">
                  <FeatureItem
                    icon={
                      <FontAwesomeIcon color="#22c55e" icon={faCircleCheck} />
                    }
                  >
                    Already pre-approved
                  </FeatureItem>
                  <FeatureItem
                    icon={<FontAwesomeIcon color="#f6a623" icon={faBox} />}
                  >
                    Custom product compensation
                  </FeatureItem>
                  <FeatureItem
                    icon={
                      <FontAwesomeIcon color="#f6a623" icon={faDollarSign} />
                    }
                  >
                    Option to add cash compensation
                  </FeatureItem>
                  <FeatureItem
                    icon={<FontAwesomeIcon color="#f6a623" icon={faMessage} />}
                  >
                    Personalized message from the business
                  </FeatureItem>
                </div>

                <div className="actions">
                  <button
                    className="btn btn--amber"
                    onClick={() => navigate("/creators")}
                  >
                    Invite Creators
                  </button>
                </div>
              </div>

              {/* Card 2 */}
              <div className="card">
                <div className="card-head">
                  <div className="avatar-stack two-row">
                    <img
                      className="avatar"
                      src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-2.jpg"
                      alt="c1"
                    />
                    <img
                      className="avatar"
                      src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-6.jpg"
                      alt="c2"
                    />
                    <img
                      className="avatar"
                      src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-4.jpg"
                      alt="c3"
                    />
                    <img
                      className="avatar"
                      src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-7.jpg"
                      alt="c3"
                    />
                    <img
                      className="avatar"
                      src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-8.jpg"
                      alt="c3"
                    />
                    <img
                      className="avatar"
                      src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-9.jpg"
                      alt="c3"
                    />
                  </div>
                  <div className="badge badge--amber">
                    <BullhornIcon />
                    {business.viewMode === BusinessCampaignCollab.CollabOnly
                      ? "Collab"
                      : "Campaign"}
                  </div>
                </div>

                <h3 className="card-title">I Want to Invite Many Influencers</h3>
                <p className="card-desc">
                  Reach the entire Mustard community in bulk. We'll send mobile
                  and email invites on your behalf asking our members to apply
                  based on your criteria.
                </p>

                <div className="features">
                  <FeatureItem
                    icon={<FontAwesomeIcon color="#f6a623" icon={faUsers} />}
                  >
                    Run across many influencers at once
                  </FeatureItem>
                  <FeatureItem
                    icon={<FontAwesomeIcon color="#f6a623" icon={faFilter} />}
                  >
                    Ability to define filters for the best match
                  </FeatureItem>
                  <FeatureItem
                    icon={
                      <FontAwesomeIcon
                        color="#f6a623"
                        icon={faClipboardCheck}
                      />
                    }
                  >
                    Review creators applications before booking
                  </FeatureItem>
                  <FeatureItem
                    icon={
                      <FontAwesomeIcon
                        color="#f6a623"
                        icon={faMobileScreenButton}
                      />
                    }
                  >
                    Mustard sends mobile & email invites on your behalf
                  </FeatureItem>
                </div>

                <div className="actions">
                  {business.viewMode === BusinessCampaignCollab.CollabOnly ? (
                    <button
                      className="btn btn--amber"
                      onClick={() => navigate("/collabs/new")}
                    >
                      Create Collab
                    </button>
                  ) : (
                    <button
                      className="btn btn--amber"
                      onClick={() => navigate("/campaign/new")}
                    >
                      Create Campaign
                    </button>
                  )}
                </div>
              </div>

              {/* Card 3 - Network Upload (new) */}
              <div className="card card--center" style={{ display: "none" }}>
                <div className="card-head" style={{ position: "relative" }}>
                  <div className="avatar-stack" aria-hidden>
                    <img
                      className="avatar"
                      src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-2.jpg"
                      alt="Creator 1"
                    />
                    <img
                      className="avatar"
                      src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-6.jpg"
                      alt="Creator 2"
                    />
                    <img
                      className="avatar"
                      src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-8.jpg"
                      alt="Creator 3"
                    />
                    <div
                      className="avatar"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#e5e7eb",
                      }}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </div>
                  </div>

                  <div
                    className="badge badge--purple"
                    style={{ position: "absolute", top: -10, right: -10 }}
                  >
                    <FontAwesomeIcon
                      icon={faUpload}
                      style={{ marginRight: 8 }}
                    />{" "}
                    Upload
                  </div>
                </div>

                <h3 className="card-title">Your Own Network Campaign</h3>
                <p className="card-desc">
                  Upload your own list of creators and run targeted campaigns
                  with creators you already know and trust.
                </p>

                <div className="features">
                  <FeatureItem
                    icon={
                      <FontAwesomeIcon
                        icon={faUpload}
                        style={{ color: "#7c3aed", width: 18 }}
                      />
                    }
                  >
                    Upload your own influencer database
                  </FeatureItem>

                  <FeatureItem
                    icon={
                      <FontAwesomeIcon
                        icon={faHandshake}
                        style={{ color: "#f6a623", width: 18 }}
                      />
                    }
                  >
                    Work with trusted creators you know
                  </FeatureItem>

                  <FeatureItem
                    icon={
                      <FontAwesomeIcon
                        icon={faTag}
                        style={{ color: "#f6a623", width: 18 }}
                      />
                    }
                  >
                    White-labeled campaign options available
                  </FeatureItem>

                  <FeatureItem
                    icon={
                      <FontAwesomeIcon
                        icon={faBullseye}
                        style={{ color: "#f6a623", width: 18 }}
                      />
                    }
                  >
                    Targeted campaigns to specific networks
                  </FeatureItem>

                  <FeatureItem
                    icon={
                      <FontAwesomeIcon
                        icon={faChartLine}
                        style={{ color: "#f6a623", width: 18 }}
                      />
                    }
                  >
                    Track performance of your network
                  </FeatureItem>
                </div>

                <div className="actions" style={{ width: "100%" }}>
                  <button className="btn btn--purple" disabled>
                    Upload Network
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );

  return (
    <div className="new-home">
      <Loader visible={loading} />
      <div className="container">
        {/* Header */}
        <div className="hero">
          <h1 className="hero-title">Welcome back, {loggedUser?.name}</h1>
        </div>

        {/* Overview Section */}
        <div className="overview-section">
          <div
            className="section-header"
            style={{ margin: 0, display: "none" }}
          >
            <h2
              className="section-title"
              style={{ margin: 0, display: "none" }}
            >
              Overview
            </h2>
            <p className="section-desc" style={{ display: "none" }}>
              Performance and content metrics at a glance
            </p>
          </div>

          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-title">
                <FontAwesomeIcon icon={faPaperPlane} className="metric-icon" />{" "}
                <span>Invites Sent</span>
              </div>
              <div className="metric-content">
                <div className="metric-value">
                  {formatNumberKMBT(invited.total)}
                </div>
                {invited.latestSentAt && (
                  <div className="metric-label">
                    {dayjs(invited.latestSentAt).fromNow()} last reach out
                  </div>
                )}
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-title">
                <FontAwesomeIcon
                  icon={faFileSignature}
                  className="metric-icon"
                />
                <span>Applications</span>
              </div>
              <div className="metric-content">
                <div className="metric-value">
                  {formatNumberKMBT(pendingReview.total)}
                </div>
                {pendingReview.total > 0 && (
                  <div
                    className="metric-label underline"
                    onClick={() => navigate("/applications?status=pending")}
                  >
                    View applications
                  </div>
                )}
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-title">
                <FontAwesomeIcon
                  icon={faCalendarCheck}
                  className="metric-icon"
                />
                <span>Creators Booked</span>
              </div>
              <div className="metric-content">
                <div className="metric-value">
                  {formatNumberKMBT(booked.total)}
                </div>
                {booked.total > 0 && (
                  <div
                    className="metric-label underline"
                    onClick={() => navigate("/applications?status=accepted")}
                  >
                    View bookings
                  </div>
                )}
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-title">
                <FontAwesomeIcon icon={faEye} className="metric-icon" />
                <span>Views</span>
              </div>
              <div className="metric-content">
                <div className="metric-value">
                  {formatNumberKMBT(invited.totalViews)}
                </div>
                <div className="metric-label">
                  Creators viewing your brand's offers
                </div>
              </div>
            </div>
          </div>

          <div className="content-performance">
            <h3 className="subsection-title">Content Performance</h3>
            <div className="performance-grid">
              <div className="performance-metric performance-placeholder">
                <div className="performance-icon">
                  <FontAwesomeIcon icon={faShare} />
                </div>
                <div className="performance-value">
                  {formatNumberKMBT(collabs.totalReach)}
                </div>
                <div className="performance-label">Reach</div>
              </div>

              <div className="performance-metric performance-placeholder">
                <div className="performance-icon">
                  <FontAwesomeIcon icon={faCircleCheck} />
                </div>
                <div className="performance-value">
                  {formatNumberKMBT(collabs.totalViews)}
                </div>
                <div className="performance-label">Plays</div>
              </div>

              <div className="performance-metric performance-placeholder">
                <div className="performance-icon">
                  <FontAwesomeIcon icon={faHeart} />
                </div>
                <div className="performance-value">
                  {formatNumberKMBT(collabs.totalLikes)}
                </div>
                <div className="performance-label">Likes</div>
              </div>

              <div className="performance-metric performance-placeholder">
                <div className="performance-icon">
                  <FontAwesomeIcon icon={faComment} />
                </div>
                <div className="performance-value">
                  {formatNumberKMBT(collabs.totalComments)}
                </div>
                <div className="performance-label">Comments</div>
              </div>

              <div className="performance-metric performance-placeholder">
                <div className="performance-icon">
                  <FontAwesomeIcon icon={faShare} />
                </div>
                <div className="performance-value">
                  {formatNumberKMBT(collabs.totalReshares)}
                </div>
                <div className="performance-label">Shares</div>
              </div>

              <div className="performance-metric performance-placeholder">
                <div className="performance-icon">
                  <FontAwesomeIcon icon={faBolt} />
                </div>
                <div className="performance-value">
                  {collabs.averageEngagementRate}%
                </div>
                <div className="performance-label">Engagement</div>
              </div>
            </div>
          </div>
        </div>

        <div className="quick-actions">
          <h2 className="section-title">Quick Actions</h2>
          <div className="actions-grid">
            <div className="action-card" onClick={() => navigate("/creators")}>
              <div className="action-icon action-icon--green">
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <div className="action-title">Invite Creators</div>
              <div className="action-desc">Browse our database and invite</div>
            </div>

            {business.viewMode === BusinessCampaignCollab.CollabOnly ? (
              <div
                className="action-card"
                onClick={() => navigate("/collabs/new")}
              >
                <div className="action-icon action-icon--amber">
                  <FontAwesomeIcon icon={faBox} />
                </div>
                <div className="action-title">New Collab</div>
                <div className="action-desc">Browse creators in bulk</div>
              </div>
            ) : (
              <div
                className="action-card"
                onClick={() => navigate("/campaign/new")}
              >
                <div className="action-icon action-icon--amber">
                  <FontAwesomeIcon icon={faBox} />
                </div>
                <div className="action-title">New Campaign</div>
                <div className="action-desc">Browse creators in bulk</div>
              </div>
            )}

            <div className="action-card" onClick={() => navigate("/reports")}>
              <div className="action-icon action-icon--purple">
                <FontAwesomeIcon icon={faClipboardCheck} />
              </div>
              <div className="action-title">View Reports</div>
              <div className="action-desc">Campaign analytics</div>
            </div>

            <div
              className="action-card"
              onClick={() => navigate("/applications?status=pending")}
            >
              <div className="action-icon action-icon--blue">
                <FontAwesomeIcon icon={faClipboardCheck} />
              </div>
              <div className="action-title">Approve Applications</div>
              <div className="action-desc">
                Review and approve collaborations
              </div>
            </div>
          </div>
        </div>

        {/* Upcoming Bookings */}
        <div className="bookings-section">
          <h2 className="section-title">Upcoming Activities</h2>

          <div className="pending-activities">
            <PendingActivities showCreatorInfo />
          </div>
          {/* <div className="bookings-list">
            {orderedYears.map((year: any) => {
              if (!upcoming[year]) return null;

              const currentYear = upcoming[year];

              const orderedMonths = monthNames.filter((month) =>
                Object.keys(currentYear).includes(month)
              );

              if (
                !orderedMonths.some((m) => (currentYear[m] || []).length > 0)
              ) {
                return null;
              }

              return (
                <Row key={year}>
                  <Col xs={24}>
                    {orderedYears.length > 1 && (
                      <h3 className="header-2">{year}</h3>
                    )}
                    <Row gutter={[32, 32]} style={{ marginBottom: 16 }}>
                      <Col md={24}>
                        {orderedMonths.map((month: any) => {
                          if (!currentYear[month]) return null;
                          const currentMonthUnfiltered = currentYear[month];

                          const upcomings = currentMonthUnfiltered || [];

                          if (upcomings.length === 0) {
                            return null;
                          }

                          return (
                            <Row key={month}>
                              <Col xs={24}>
                                <h3 className="header-2">
                                  {month.charAt(0).toUpperCase() +
                                    month.slice(1)}
                                </h3>

                                <Row gutter={[32, 32]}>
                                  {upcomings.map((collab: any) => {
                                    const now = dayjs();
                                    const canRescheduleAccepted = dayjs(
                                      collab?.applicationDate?.date
                                    ).isAfter(now.add(48, "hour"));

                                    return (
                                      <Col
                                        xs={24}
                                        key={`APPROVED_${collab.bookingId}`}
                                      >
                                        <div className="application">
                                          <div
                                            style={{
                                              display: "flex",
                                              gap: 16,
                                              alignItems: "center",
                                              flex: isMobile ? "none" : 3,
                                            }}
                                          >
                                            <div
                                              style={{
                                                position: "relative",
                                                cursor: "pointer",
                                              }}
                                              // onClick={() =>
                                              //   onClickFoodie(collab.foodieId)
                                              // }
                                            >
                                              <Avatar
                                                src={collab.avatarUrl}
                                                size={60}
                                              />
                                              {collab.isPremium && (
                                                <span className="diamond">
                                                  <CrownOutlined />
                                                </span>
                                              )}
                                            </div>
                                            <div>
                                              <span className="name">
                                                {collab?.username}
                                              </span>
                                              <span
                                                style={{ marginRight: 8 }}
                                                className="text-regular-small"
                                              >
                                                <InstagramOutlined
                                                  style={{ marginRight: 4 }}
                                                />
                                                {formatNumberKMBT(
                                                  collab?.instagramFollowersCount
                                                )}
                                              </span>
                                              <span className="text-regular-small">
                                                <TikTokOutlined
                                                  style={{ marginRight: 4 }}
                                                />
                                                {formatNumberKMBT(
                                                  collab?.tiktokFollowersCount
                                                )}
                                              </span>
                                            </div>
                                          </div>
                                          <div
                                            className="box-icon"
                                            style={{
                                              flex: isMobile ? "none" : 3,
                                            }}
                                          >
                                            <LocationIcon className="svg-16" />
                                            <div>
                                              <span
                                                className="name ellipsis"
                                                style={{ width: 270 }}
                                              >
                                                {collab?.venue?.name}
                                              </span>
                                              <span
                                                className="value ellipsis"
                                                style={{ width: 270 }}
                                              >
                                                {collab?.venue.addressDisplay ||
                                                  collab?.venue?.address}
                                              </span>
                                            </div>
                                          </div>
                                          <div
                                            className="box-icon"
                                            style={{
                                              flex: isMobile ? "none" : 1,
                                            }}
                                          >
                                            <ClockIcon />
                                            <div>
                                              <span className="name">
                                                {collab?.applicationDate?.hour}
                                              </span>
                                              <span className="value">
                                                {dayjs
                                                  .utc(
                                                    collab?.applicationDate
                                                      ?.date
                                                  )
                                                  .format("DD, MMMM, YYYY")}
                                              </span>
                                            </div>
                                          </div>
                                          <div
                                            className="box-icon"
                                            style={{
                                              flex: isMobile ? "none" : 2,
                                            }}
                                          >
                                            <TicketIcon />
                                            <div>
                                              <span className="name">
                                                {collab?.securityCode}{" "}
                                                {collab?.deliveryAddress &&
                                                  ` ∘  ${collab?.deliveryAddress}`}
                                              </span>
                                              <span className="value">
                                                ${collab?.foodCompAmount}
                                                {" credit "}
                                                {collab?.guestAllowed &&
                                                  " ∘ 1 guest"}
                                              </span>
                                            </div>
                                          </div>
                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "center",
                                              flexDirection: "column",
                                              gap: 6,
                                            }}
                                          >
                                            <Tooltip title="Chat with Influencer">
                                              <Button
                                                style={{
                                                  display: isMobile
                                                    ? "none"
                                                    : "flex",
                                                  fontSize: 12,
                                                  height: 40,
                                                }}
                                                className="button-icon"
                                                icon={
                                                  <MessagesIcon
                                                    style={{ width: 12 }}
                                                  />
                                                }
                                                onClick={() =>
                                                  onClickMessage(collab)
                                                }
                                              >
                                                Chat
                                              </Button>
                                            </Tooltip>
                                            {canRescheduleAccepted && (
                                              <RescheduleRequest
                                                influencer={collab}
                                                asButton
                                              />
                                            )}
                                          </div>
                                        </div>
                                        <div className="extra-info">
                                          <div
                                            style={{
                                              flex: 1,
                                              display: "flex",
                                              gap: 16,
                                              justifyContent: isMobile
                                                ? "center"
                                                : "flex-start",
                                            }}
                                          >
                                            <span className="text-strong-small flex-gap-4">
                                              <PurchaseIcon />{" "}
                                              {!isMobile && "Purchase Intent: "}
                                              {collab?.dinnerIntentByPosts ||
                                                "-"}
                                              /post
                                            </span>
                                            <span className="text-strong-small flex-gap-4">
                                              <FlashIcon />{" "}
                                              {!isMobile && "Engagement: "}{" "}
                                              {collab?.engagementRate || "-"}%
                                            </span>
                                            <span className="text-strong-small flex-gap-4">
                                              <StarIcon />{" "}
                                              {!isMobile && "Rating: "}{" "}
                                              {collab?.commitmentRate || "-"}%
                                            </span>
                                          </div>
                                        </div>
                                      </Col>
                                    );
                                  })}
                                </Row>
                              </Col>
                            </Row>
                          );
                        })}
                      </Col>
                    </Row>
                  </Col>
                </Row>
              );
            })}
          </div> */}
        </div>

        {/* Created Posts */}
        {sortedPosts.length > 0 && (
          <div className="content-section">
            <div className="content-header">
              <div className="content-title-row">
                <h2 className="section-title">Created Posts</h2>
                {/* <FontAwesomeIcon icon={faFilter} className="dropdown-icon" /> */}
              </div>
              <div className="content-controls">
                <div className="sort-select">
                  <span>Sort by:</span>
                  <select value={sortBy} onChange={handleSortChange}>
                    <option value="latest">Latest</option>
                    <option value="performance">Performance</option>
                    <option value="engagement">Engagement</option>
                    <option value="reach">Reach</option>
                    <option value="intent">Purchase Intent</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="posts-container">
              <div className="posts-scroll-controls">
                <button
                  className="scroll-btn scroll-btn--left"
                  onClick={() => scrollPosts("left")}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button
                  className="scroll-btn scroll-btn--right"
                  onClick={() => scrollPosts("right")}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
              <div className="posts-grid" ref={postsContainerRef}>
                {sortedPosts.map((post, index) => (
                  <div
                    className="post-card"
                    key={`POST_${index}_${post.socialMediaUrl}`}
                    onClick={() => onClickOpenPost(post.socialMediaUrl)}
                  >
                    <div className="post-image-container">
                      <img
                        src={post.coverImg}
                        alt={post.socialMediaName}
                        className="post-image"
                      />
                      <div className="post-platform">
                        {post.socialMediaName?.toLowerCase() === "tiktok" ? (
                          <TikTokIcon stroke="#fff" />
                        ) : (
                          <InstagramIcon fill="#fff" />
                        )}
                      </div>
                      <div className="post-username">@{post.ownerUsername}</div>
                    </div>
                    <div className="post-details">
                      <div className="post-header">
                        <span className="post-title">{post.venueName}</span>
                        <span className="post-date">
                          {dayjs(post.createdAt).format("MMM D, YYYY")}
                        </span>
                      </div>
                      <div className="post-metrics">
                        <div className="metric-item">
                          <FontAwesomeIcon icon={faBolt} color="#f6a623" />
                          <span>{post.engagementRate || "-"}% Eng</span>
                        </div>
                        <div className="metric-item">
                          <FontAwesomeIcon icon={faPlay} />
                          <span>
                            {formatNumberKMBT(post.reach) || "-"} Views
                          </span>
                        </div>
                        <div className="metric-item">
                          <FontAwesomeIcon icon={faHeart} color="#ef4444" />
                          <span>
                            {formatNumberKMBT(post.likes) || "-"} Likes
                          </span>
                        </div>
                        <div className="metric-item">
                          <FontAwesomeIcon
                            icon={faCartShopping}
                            color="#52c41a"
                          />
                          <span>{post.dinner || "-"} Intent</span>
                        </div>
                      </div>
                      <div className="performance-score">
                        <div className="score-label">Performance Score</div>
                        <div className="score-bar">
                          <div
                            className="score-progress"
                            style={{
                              width: `${post.performanceScore || 0}%`,
                              backgroundColor: performanceScoreColors(
                                post.performanceScore || 0,
                              ),
                            }}
                          ></div>
                          <span
                            className="score-value"
                            style={{
                              color: performanceScoreColors(
                                post.performanceScore || 0,
                              ),
                            }}
                          >
                            {post.performanceScore || "-"}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {/* 
              {[...Array(2)].map((_, i) => (
                <div key={i} className="post-card post-card--placeholder">
                  <div className="post-image-container">
                    <div className="post-image-placeholder"></div>
                  </div>
                  <div className="post-details">
                    <div className="post-placeholder-content"></div>
                  </div>
                </div>
              ))} */}
              </div>

              {/* Content Analytics */}
              <div className="content-analytics" style={{ display: "none" }}>
                <div className="analytics-header">
                  <h3 className="subsection-title">Content Analytics</h3>
                  <button className="view-report-btn">View Full Report</button>
                </div>
                <div className="analytics-grid">
                  <div className="analytic-item">
                    <div className="analytic-value">24</div>
                    <div className="analytic-label">Total Posts</div>
                    <div className="analytic-change positive">+3 this week</div>
                  </div>
                  <div className="analytic-item">
                    <div className="analytic-value">8.2%</div>
                    <div className="analytic-label">Avg Engagement</div>
                    <div className="analytic-change positive">
                      +1.4% vs last month
                    </div>
                  </div>
                  <div className="analytic-item">
                    <div className="analytic-value">42</div>
                    <div className="analytic-label">Total Purchase Intent</div>
                    <div className="analytic-change positive">
                      +12 this week
                    </div>
                  </div>
                  <div className="analytic-item">
                    <div className="analytic-value">81%</div>
                    <div className="analytic-label">Avg Performance Score</div>
                    <div className="analytic-change positive">
                      +5% vs last month
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recent Activity */}
        <div className="activity-section" style={{ display: "none" }}>
          <h2 className="section-title">Recent Activity</h2>
          <div className="activity-container">
            <div className="activity-list">
              <div className="activity-item">
                <div className="activity-icon activity-icon--success">
                  <FontAwesomeIcon icon={faCircleCheck} />
                </div>
                <div className="activity-content">
                  <div className="activity-title">New booking confirmed</div>
                  <div className="activity-desc">
                    @wickeddates accepted your collaboration request
                  </div>
                  <div className="activity-time">2 hours ago</div>
                </div>
              </div>

              <div className="activity-item">
                <div className="activity-icon activity-icon--amber">
                  <FontAwesomeIcon icon={faMessage} />
                </div>
                <div className="activity-content">
                  <div className="activity-title">Content posted</div>
                  <div className="activity-desc">
                    @happyfoodietown shared their restaurant visit
                  </div>
                  <div className="activity-time">4 hours ago</div>
                </div>
              </div>

              <div className="activity-item">
                <div className="activity-icon activity-icon--blue">
                  <FontAwesomeIcon icon={faMessage} />
                </div>
                <div className="activity-content">
                  <div className="activity-title">Invites sent</div>
                  <div className="activity-desc">
                    15 new collaboration invites were sent to potential creators
                  </div>
                  <div className="activity-time">6 hours ago</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewHome;
